import { DEBUG } from "../assets";
import { config } from "../config";
import type { CourseId, Mode } from "../config/types";
import { debugParams } from "../core/debugParams";
import { clearAll, emit, on } from "../core/events";
import { k } from "../core/kaplay";
import { randomSeed } from "../core/rng";
import { createRunState } from "../core/runState";
import { autopilot } from "../components/autopilot";
import { lookLabels } from "../components/look";
import { scoreKeeper } from "../components/scoreKeeper";
import { addChunks } from "../entities/chunks";
import { addGround } from "../entities/ground";
import { registerHazards } from "../entities/hazards";
import { addPlayer, addShadow } from "../entities/player";
import { addTyreTracks } from "../entities/tyreTracks";
import { getDifficulty } from "../systems/difficulty";
import { createMountain } from "../systems/mountain";
import { courseLength } from "../systems/zones";
import { isTouchDevice } from "../core/settings";
import { addDebugOverlay } from "../ui/debugOverlay";
import { showTouchControls } from "../ui/touchControls";

export interface GameSceneArgs {
    mode?: Mode;
    course?: CourseId | null;
}

function courseSetup(course: CourseId | null) {
    const courseConfig = config.courses.courses.find((c) => c.id === course);
    if (!courseConfig) {
        const { cycle, aggression } = config.courses.freeride;
        return { legs: cycle, endless: true, seed: randomSeed(), grade: null, aggression };
    }
    const { legs, seed, grade, bigfoot } = courseConfig;
    return { legs, endless: false, seed, grade, aggression: bigfoot.aggression };
}

export function gameScene(args: GameSceneArgs = {}) {
    const mode = debugParams.mode ?? args.mode ?? "descent";
    const course = mode === "freeride" ? null : (debugParams.course ?? args.course ?? 1);
    const setup = courseSetup(course);
    const seed = debugParams.seed ?? setup.seed;

    const mountain = createMountain(seed, setup.legs, setup.endless, config.zones.zones);
    const difficultyAt = (yM: number) => getDifficulty(
        { zone: mountain.zoneAt(yM), grade: setup.grade, distanceM: yM, bigfootAggression: setup.aggression },
        config.difficulty,
        config.modes.modes[mode],
    );
    const run = createRunState(mode, course, seed);
    run.zone = mountain.zoneAt(0).zone;
    run.surface = mountain.surfaceAt(0, 0);

    k.onSceneLeave(clearAll);
    showTouchControls(isTouchDevice());
    k.onSceneLeave(() => showTouchControls(false));
    emit("runStart", { mode, course, seed });
    run.phase = "running";

    registerHazards(mountain);
    addGround(mountain);
    const player = addPlayer(run, mountain, () => chunks.regions);
    addShadow(player);
    const startY = debugParams.startM ?? 0;
    player.motion = { ...player.motion, x: mountain.trailAt(startY)?.x ?? 0, y: startY };
    run.distanceM = startY;
    run.zonesReached = [mountain.zoneAt(startY).zone];
    k.add([scoreKeeper(run)]);
    const chunks = addChunks(mountain, setup.endless ? null : courseLength(setup.legs), difficultyAt);
    run.difficulty = difficultyAt(startY);
    k.loop(1, () => (run.difficulty = difficultyAt(run.distanceM)));
    addTyreTracks(player, run);

    if (DEBUG) {
        addDebugOverlay(player, run);
        if (debugParams.autopilot) player.use(autopilot(mountain, debugParams.autopilot === "side"));
        if (debugParams.timeScale) k.debug.timeScale = debugParams.timeScale;
        lookLabels.visible = false;
        k.onKeyPress("f2", () => (lookLabels.visible = !lookLabels.visible));
    }

    on("runEnd", ({ reason }) => {
        run.phase = "ended";
        k.go("results", { reason });
    });

    k.onUpdate(() => {
        run.timeS += k.dt();
        if (player.intent.pause) emit("runEnd", { reason: "quit" });
    });

    if (DEBUG) {
        k.onKeyPress("x", () => player.wipeout("medium"));
        k.onKeyPress("c", () => player.wipeout("large"));
        k.onKeyPress("l", () => player.launch(6));
    }
}
